import { useEffect } from 'react';
import { shallowEqual } from 'react-redux';

import { ICardCreationModalProps } from '../components/CardUnitCreationModal';

import { selectProfileWalletsByUserId, selectWalletsByUserId } from '@/store/selectors';
import { openModal } from '@/store/actions/core.actions';
import { useAppSelector, useAppDispatch } from '@/hooks/redux-helpers';
import { useDashboardConfig } from '@/hooks/useDashboardConfig';
import { IWallet, walletStatusList } from '@/features/wallets/store/models/wallet.model';
import { getWallets } from '@/features/wallets/store/actions/wallet.actions';
import { IUser } from '@/features/user/store/user.model';
import { TDashboardConfigSettingsCardsDesignType } from '@/store/models/dashboard-config.model';
import { DashboardHelpers } from '@/utils/DashboardHelpers';
import { useUserWalletInfo } from '@/features/cards/hooks/useUserWalletInfo';
import { useCardUserInfo } from '@/features/cards/hooks/useCardUserInfo';
import { TWalletSelector } from '@/features/cards/store/types/card.types';

export function useWalletList(user: IUser) {
  const dispatch = useAppDispatch();
  const config = useDashboardConfig();

  const cardDesigns = config?.settings?.cards?.designs || [];
  const designTypes = [...new Set(cardDesigns.map((design) => design.type))];

  const { userData: parentUser, isLoading: isFetchingParentData } = useCardUserInfo({
    userId: user?.parentUserId,
  });

  const { userWalletData: parentWallets, isLoading: isParentWalletLoading } = useUserWalletInfo({
    userId: user?.parentUserId,
  });

  const walletMapping = (wallets: IWallet[], user: IUser): TWalletSelector[] => {
    return (wallets || [])
      .filter((wallet) => wallet.walletStatus === walletStatusList.VALIDATED)
      .map((wallet) => ({
        eventName: wallet?.eventName,
        walletId: wallet.walletId,
        name: DashboardHelpers.getUserName(user),
        authorizedBalance: wallet?.authorizedBalance,
        iban: wallet?.iban,
      }));
  };

  const isLoading = useAppSelector((state) => selectProfileWalletsByUserId(state, user.userId)?.isLoading);
  const userWallets = useAppSelector((state) => selectWalletsByUserId(state, user.userId), shallowEqual) || [];

  useEffect(() => {
    dispatch(getWallets(user.userId, false));
  }, [user.userId, dispatch]);

  const wallets = [...walletMapping(userWallets, user), ...walletMapping(parentWallets, parentUser)];

  const hasUserInformation =
    !!user?.firstname &&
    !!user?.lastname &&
    !!user?.address1 &&
    !!user?.postcode &&
    !!user?.city &&
    !!user?.country &&
    (!!user?.mobile || !!user?.phone);

  const isDisabled = wallets?.length === 0 || !hasUserInformation;

  const createCard = (designType: TDashboardConfigSettingsCardsDesignType) => {
    dispatch(openModal<ICardCreationModalProps>('cardUnitCreationModal', { designType, wallets, user, parentUser }));
  };

  return {
    designTypes,
    isLoading: isLoading || isParentWalletLoading || isFetchingParentData,
    hasUserInformation,
    isDisabled,
    createCard,
  };
}
